'use strict';

// CSRF対策: 全てのPOSTリクエストにトークンを自動付与する（個々のfetch呼び出し側の変更は不要）
// #csrf-token はログイン中のページ（header.php/settingsCommon.php）にのみ存在するため、無い場合は何もしない
(function installCsrfFetchGuardFunc() {
    const csrfToken = (document.getElementById('csrf-token') as HTMLInputElement | null)?.value || '';
    if (!csrfToken) return;
    const originalFetch = window.fetch.bind(window);
    window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
        if (method === 'POST') {
            const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
            headers.set('X-CSRF-Token', csrfToken);
            init = { ...init, headers };
        }
        return originalFetch(input, init);
    };
})();

// 共通モーダルUI: alert()/confirm()の代替（ブラウザごとに見た目が異なる標準ダイアログを統一するため）。
// import/export文が無いためTypeScript上もグローバルスクリプト扱いとなり、settings.ts側からも
// 型宣言なしにそのまま呼び出せる（common.min.jsがtype="module"無しで先に読み込まれる構成に依存）。
let customModalElements: {
    overlay: HTMLElement;
    message: HTMLElement;
    okButton: HTMLButtonElement;
    cancelButton: HTMLButtonElement;
} | null = null;

function ensureCustomModalFunc() {
    if (customModalElements) return customModalElements;

    const overlay = document.createElement('div');
    overlay.id = 'custom-modal-overlay';
    overlay.className = 'custom-modal-overlay is-hide';
    overlay.innerHTML = `
        <div class="custom-modal" role="alertdialog" aria-modal="true">
            <p class="custom-modal__message"></p>
            <div class="custom-modal__buttons">
                <button type="button" class="custom-modal__button custom-modal__button--cancel">キャンセル</button>
                <button type="button" class="custom-modal__button custom-modal__button--ok">OK</button>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    customModalElements = {
        overlay,
        message: overlay.querySelector('.custom-modal__message') as HTMLElement,
        okButton: overlay.querySelector('.custom-modal__button--ok') as HTMLButtonElement,
        cancelButton: overlay.querySelector('.custom-modal__button--cancel') as HTMLButtonElement,
    };
    return customModalElements;
}

// 「。」で終わる文ごとに改行を挟んでcontainerへ追加する（読みやすさのため）。
// innerHTMLは使わずtextContent/createElementのみで組み立て、XSSのリスクを避ける
function appendTextWithLineBreaksFunc(container: HTMLElement, text: string): void {
    const parts = text.split('。');
    parts.forEach((part, index) => {
        const isLast = index === parts.length - 1;
        const segment = isLast ? part : part + '。';
        if (segment === '') return;
        container.appendChild(document.createTextNode(segment));
        if (!isLast && part !== '') {
            container.appendChild(document.createElement('br'));
        }
    });
}

function showCustomModalFunc(message: string, showCancel: boolean): Promise<boolean> {
    const modal = ensureCustomModalFunc();

    // 「エラー: 〇〇」形式のメッセージは「エラー」を赤字の見出しとして改行表示する
    modal.message.textContent = '';
    const errorPrefixMatch = message.match(/^エラー[:：]\s*/);
    if (errorPrefixMatch) {
        const errorLabel = document.createElement('span');
        errorLabel.className = 'custom-modal__error-label';
        errorLabel.textContent = 'エラー';
        modal.message.appendChild(errorLabel);
        modal.message.appendChild(document.createElement('br'));
        appendTextWithLineBreaksFunc(modal.message, message.slice(errorPrefixMatch[0].length));
    } else {
        appendTextWithLineBreaksFunc(modal.message, message);
    }

    modal.cancelButton.hidden = !showCancel;
    modal.overlay.classList.remove('is-hide');

    // モーダル表示中は背景のスクロールを止める。この時スクロールバーが消えて本文の幅が
    // 広がり、ページがガタつくため、消えるスクロールバー分だけpadding-rightで補う
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add('is-modalOpen');

    return new Promise((resolve) => {
        const cleanupFunc = (): void => {
            modal.overlay.classList.add('is-hide');
            document.body.classList.remove('is-modalOpen');
            document.body.style.paddingRight = '';
            modal.okButton.removeEventListener('click', onOkFunc);
            modal.cancelButton.removeEventListener('click', onCancelFunc);
            document.removeEventListener('keydown', onKeydownFunc);
        };
        const onOkFunc = (): void => {
            cleanupFunc();
            resolve(true);
        };
        const onCancelFunc = (): void => {
            cleanupFunc();
            resolve(false);
        };
        const onKeydownFunc = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                cleanupFunc();
                resolve(false);
            }
        };
        modal.okButton.addEventListener('click', onOkFunc);
        modal.cancelButton.addEventListener('click', onCancelFunc);
        document.addEventListener('keydown', onKeydownFunc);
    });
}

// alert()の代替。OKボタンのみ表示する
function showAlertFunc(message: string): Promise<void> {
    return showCustomModalFunc(message, false).then(() => undefined);
}

// confirm()の代替。OK/キャンセル両方表示し、OKでtrue・キャンセル/Escでfalseを返す
function showConfirmFunc(message: string): Promise<boolean> {
    return showCustomModalFunc(message, true);
}

document.addEventListener('DOMContentLoaded', function (): void {
    const bodyElement = document.querySelector('body');

    // ログアウトリンク: GETでの直接ミューテーションを避けるため、CSRFトークン付きのPOSTで実行
    // （全端末分のログイン状態をまとめて無効化する仕様。片方の端末だけを残す機能は無い）
    const logoutLink = document.getElementById('logout-link');
    if (logoutLink) {
        logoutLink.addEventListener('click', (e: MouseEvent) => {
            e.preventDefault();
            fetch('/logout.php', { method: 'POST' })
                .then(() => { location.href = '/index.php'; })
                .catch(() => { location.href = '/index.php'; });
        });
    }

    /* ==========================================================================
    ホーム一覧ページ（home.php）
    ========================================================================== */
    if (bodyElement && bodyElement.classList.contains('page-home')) {
        // スペースIDを入力
        const inputId = document.getElementById('space-id__input') as HTMLInputElement;
        inputId.addEventListener('keyup', (e: KeyboardEvent) => {
            // 入力が空でない場合、ボタンを有効にする
            const submitButton = document.getElementById('space-id__submit') as HTMLButtonElement;
            const classButton = document.querySelector('.main-common__button') as HTMLElement;
            const target = e.target as HTMLInputElement;
            if (target.value.length > 0) {
                //入力された文字が1文字以上なら実行される
                submitButton.disabled = false;
                classButton.classList.remove('is-disabled');
            } else {
                submitButton.disabled = true;
                classButton.classList.add('is-disabled');
            }
        });

        // スペースIDのフォームが送信された時に実行
        const spaceIdForm = document.querySelector('form') as HTMLFormElement;
        spaceIdForm.addEventListener('submit', function (e: SubmitEvent) {
            if (inputId && inputId.value.trim() === '') {
                e.preventDefault(); // フォーム送信をキャンセル
                return;
            }
            if (inputId && !/^[a-zA-Z0-9]+$/.test(inputId.value.trim())) {
                e.preventDefault();
                showAlertFunc('スペースIDは半角英数字のみ使用できます。');
            }
        });

        // スペースID名の変更エリアを表示
        // 現在開いている編集セクションをJS変数で追跡（毎クリックのDOM走査を回避）
        let currentOpenSection: HTMLElement | null = null;

        function closeCurrentSectionFunc(): void {
            if (!currentOpenSection) return;
            currentOpenSection.classList.remove('is-show');
            currentOpenSection.closest('li')?.querySelector('figcaption')?.classList.remove('is-hide');
            currentOpenSection = null;
        }

        document.querySelectorAll<HTMLElement>('.clickable-edit').forEach((figcaption) => {
            figcaption.addEventListener('click', function (this: HTMLElement, e: MouseEvent) {
                e.stopPropagation();
                const index = this.getAttribute('data-index');
                const editSection = document.getElementById(`edit-section-${index}`) as HTMLElement;
                // 他の開いている編集エリアを先に閉じる
                if (currentOpenSection && currentOpenSection !== editSection) {
                    closeCurrentSectionFunc();
                }
                editSection.classList.toggle('is-show');
                this.parentElement?.classList.toggle('is-hide');
                currentOpenSection = editSection.classList.contains('is-show') ? editSection : null;
            });
        });

        // 編集エリア内のクリックは閉じない
        document.querySelectorAll<HTMLElement>('.page-home__lists-edit').forEach((editSection) => {
            editSection.addEventListener('click', function (e: MouseEvent) {
                e.stopPropagation();
            });
        });

        // 編集エリア外クリックで全て閉じる
        document.addEventListener('click', function () {
            closeCurrentSectionFunc();
        });

        // スペースID名の変更して保存
        document.querySelectorAll<HTMLButtonElement>('.settings-main__button').forEach((button) => {
            const handleSaveFunc = async (index: string): Promise<void> => {
                const editSection = document.getElementById(`edit-section-${index}`) as HTMLElement;
                const editNameElem = document.getElementById(`edit-name-${index}`) as HTMLInputElement;
                const spaceId = (document.getElementById(`edit-spaceid-${index}`) as HTMLInputElement).value;
                const editName = editNameElem.value;
                const liElement = editSection.closest('li') as HTMLElement;
                const figcaption = liElement.querySelector('figcaption') as HTMLElement;

                if (editName.trim() === '') {
                    await showAlertFunc('スペースIDの新しい名称を入力してください。');
                    return;
                }

                const formData = new FormData();
                formData.append('spaceid', spaceId);
                formData.append('name', editName);

                try {
                    const response = await fetch('/api/spaceIdEdit.php', {
                        method: 'POST',
                        cache: 'no-store',
                        body: formData
                    });
                    if (response.ok) {
                        await showAlertFunc('スペースIDの新しい名称を更新しました。');
                        const spanTag = figcaption.querySelector('.is-txtHoverNnderline');
                        if (spanTag) spanTag.textContent = editName;
                        editNameElem.placeholder = editName;
                        editNameElem.value = '';
                        figcaption.classList.remove('is-hide');
                        editSection.classList.remove('is-show');

                    } else {
                        await showAlertFunc('スペースIDの新しい名称の更新に失敗しました。');
                    }
                } catch (error) {
                    await showAlertFunc('保存中にエラーが発生しました。');
                }
            };

            // 保存ボタンクリック時のイベント
            button.addEventListener('click', function (this: HTMLButtonElement) {
                const index = this.id.split('-').pop() as string;
                handleSaveFunc(index);
            });

            // 入力欄でENTERキーが押された時のイベント
            const index = button.id.split('-').pop() as string;
            const editNameElem = document.getElementById(`edit-name-${index}`) as HTMLInputElement;
            editNameElem.addEventListener('keydown', function (e: KeyboardEvent) {
                if (e.key === 'Enter' && !e.isComposing) {
                    e.preventDefault();
                    handleSaveFunc(index);
                }
            });
        });

        // スペースIDを削除
        document.querySelectorAll<HTMLElement>('.clickable-delete').forEach((deleteButton) => {
            deleteButton.addEventListener('click', async function (this: HTMLElement) {
                const spaceName = this.closest('figcaption')?.querySelector('.is-txtHoverNnderline')?.textContent ?? 'このスペースID';
                const confirmDelete = await showConfirmFunc(`本当に「${spaceName}」を削除しますか？`);
                if (!confirmDelete) {
                    return;
                }

                try {
                    const index = this.getAttribute('data-index');
                    const spaceId = (document.getElementById(`edit-spaceid-${index}`) as HTMLInputElement).value;

                    const formData = new FormData();
                    formData.append('spaceId', spaceId);

                    const response = await fetch('/settings/logic-delete/spaceIdDelete.php', {
                        method: 'POST',
                        cache: 'no-store',
                        body: formData
                    });

                    if (!response.ok) {
                        throw new Error(`HTTPエラー: ${response.status}`);
                    }

                    const result: { success: boolean; message: string } = await response.json();

                    if (result.success) {
                        await showAlertFunc(result.message);
                        window.location.href = "/home.php";
                    } else {
                        await showAlertFunc(result.message);
                    }
                } catch (error) {
                    await showAlertFunc('削除処理中にエラーが発生しました。');
                }
            });
        });
    } else if (bodyElement && bodyElement.classList.contains('page-addspace__id')) {
        /* ==========================================================================
        スペースIDを元に登録（addspaceId.php）
        ========================================================================== */
        //スペースID入力で登録
        const registBtn = document.getElementById('regist-id');
        if (registBtn) {
            registBtn.addEventListener('click', async () => {
                const figcaptionElem = document.querySelector('.page-addspace__id-figure figcaption') as HTMLElement;
                const spaceId = figcaptionElem.getAttribute('id') as string;
                const name = figcaptionElem.textContent as string;
                const url = (document.querySelector('.page-addspace__id-figure--thumb img') as HTMLImageElement).getAttribute('src') as string;

                try {
                    const checkResp = await fetch('/api/spaceIdCheck.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({ spaceID: spaceId }).toString()
                    });
                    const checkResult = await checkResp.text();

                    if (checkResult === "1") {
                        await showAlertFunc("既に存在しているスぺースIDです。");
                        window.location.href = "/home.php";
                        return;
                    }

                    await fetch('/api/mkdir.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({ spaceID: spaceId, name, url }).toString()
                    });
                    await showAlertFunc("スぺースIDの登録が完了しました。");
                    window.location.href = "/home.php";
                } catch (error) {
                    await showAlertFunc('保存中にエラーが発生しました。');
                }
            });
        }
    }

    /* ==========================================================================
    inputテキストのクリック時のアニメーション、ショーケースCSSプロパティ追加
    ========================================================================== */
    document.addEventListener('focusout', function (e: FocusEvent) {
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT') return;
        if ((target as HTMLInputElement).value.trim()) {
            target.classList.add('is-used');
        } else {
            target.classList.remove('is-used');
        }
    });

    // ショーケースの中に特定のCSSプロパティを追加
    if (document.documentElement.classList.contains('is-showcase')) return;

    setTimeout(() => {
        const showcaseIframe = document.getElementById('showcase') as HTMLIFrameElement | null;
        if (!showcaseIframe) return;

        showcaseIframe.onload = function (): void {
            try {
                const iframeDocument = showcaseIframe.contentDocument || showcaseIframe.contentWindow?.document;
                if (!iframeDocument) return;
                const styleElement = iframeDocument.createElement('style');
                styleElement.textContent = '.bottom-ui { display: none; }';
                (iframeDocument.head || iframeDocument.body).appendChild(styleElement);
            } catch (e) {
                showAlertFunc('ショーケースにエラーが発生しました。');
            }
        };

        if (showcaseIframe.contentWindow?.document.readyState === 'complete') {
            (showcaseIframe.onload as () => void)();
        }
    }, 2000);
});
